import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Download, LogOut, RefreshCw, Users, Building2, User as UserIcon, Activity, LayoutGrid, Settings as SettingsIcon, Car as CarIcon } from "lucide-react";
import { api } from "@/lib/auth";
import { useClientAuth, formatApiError } from "@/lib/clientAuth";
import Logo from "@/components/Logo";
import { StatCard, StatusBadge, SyncHealthBanner, fmtDateTime, STATUS_META } from "@/components/portal/parts";
import TeamPanel from "@/components/portal/TeamPanel";
import NotificationBell from "@/components/portal/NotificationBell";
import NotificationSettings from "@/components/portal/NotificationSettings";
import ComingSoon from "@/pages/portal/ComingSoon";
import CorporatePortal from "@/pages/portal/CorporatePortal";
import { ReservationListCard, ReservationDetailDrawer } from "@/components/portal/ReservationDetail";
import { DateField } from "@/components/portal/DateField";

const sel = "rounded-lg border border-gold/20 bg-obsidian-900 px-3 py-2 text-xs text-ivory outline-none focus:border-gold/60";

export default function Dashboard() {
  const { user, logout } = useClientAuth();
  const nav = useNavigate();
  const [scope, setScope] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [tab, setTab] = useState("operations");
  const [filters, setFilters] = useState({ q: "", status: "", chauffeur: "", vehicle: "", date_from: "", date_to: "" });
  const [showCorpReq, setShowCorpReq] = useState(false);
  const [health, setHealth] = useState(null);

  useEffect(() => { if (user === false) nav("/login"); }, [user, nav]);

  // Managers land on their company's Live Operations Board by default.
  const inited = useRef(false);
  useEffect(() => {
    if (!user || inited.current) return;
    inited.current = true;
    const mgr = (user.memberships || []).find((m) => ["corporate_admin", "corporate_dispatcher"].includes(m.role));
    if (mgr) { setScope(mgr.company_id); setTab("operations"); }
    else if (user.is_driver) { setScope("driver"); setTab("reservations"); }
  }, [user]);

  const query = useMemo(() => {
    const p = new URLSearchParams();
    if (scope === "individual") p.set("scope", "individual");
    else if (scope !== "all") p.set("company", scope);
    Object.entries(filters).forEach(([k, v]) => { if (v) p.set(k, v); });
    return p;
  }, [scope, filters]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/reservations?${query.toString()}`);
      setData(data);
      api.get("/reservations/sync-health").then(({ data }) => setHealth(data)).catch(() => {});
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load reservations");
    } finally { setLoading(false); }
  }, [query]);

  useEffect(() => { if (user) load(); }, [user, load]);

  // Operations Board auto-refresh (near-real-time, respects provider limits)
  useEffect(() => {
    if (!user || tab !== "operations") return;
    const id = setInterval(() => { load(); }, 30000);
    return () => clearInterval(id);
  }, [user, tab, load]);

  const openDetail = async (id) => {
    try { const { data } = await api.get(`/reservations/${id}`); setDetail(data); }
    catch { toast.error("Could not open reservation"); }
  };

  const exportCsv = async () => {
    try {
      const { data } = await api.get(`/reservations/export.csv?${query.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = "reservations.csv"; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  const requestCorp = async (name) => {
    try {
      const { data } = await api.post("/corporate/register", { company_name: name });
      toast.success(data.message); setShowCorpReq(false);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (!user) return <main className="flex min-h-screen items-center justify-center bg-obsidian-900"><p className="font-mono text-xs uppercase tracking-[0.3em] text-gold">Loading…</p></main>;

  // Client reservation portal is behind a feature flag. While it's not live, every signed-in
  // client sees a Coming Soon page instead of the (unfinished) reservation dashboard. The
  // backend independently returns no reservation data, so direct URLs/APIs reveal nothing.
  if (user.portal_live === false) return <ComingSoon />;

  // Phase B: corporate members get the redesigned luxury Corporate Client Portal
  // (hard-split, company-isolated). Individuals and drivers keep the existing dashboard.
  if ((user.memberships || []).some((m) => ["corporate_admin", "corporate_dispatcher", "corporate_employee"].includes(m.role))) {
    return <CorporatePortal />;
  }

  const memberships = user.memberships || [];
  const activeCompany = memberships.find((m) => m.company_id === scope);
  const isManager = activeCompany && ["corporate_admin", "corporate_dispatcher"].includes(activeCompany.role);
  const isCorpAdmin = activeCompany && activeCompany.role === "corporate_admin";
  const s = data?.stats || { upcoming: 0, active: 0, completed: 0, canceled: 0, total: 0 };

  return (
    <main className="grain min-h-screen bg-obsidian-900 text-ivory" data-testid="portal-dashboard">
      {/* header */}
      <header className="sticky top-0 z-20 border-b border-gold/15 bg-obsidian-900/90 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
          <Logo className="h-8" />
          <div className="flex items-center gap-3">
            <NotificationBell onOpenReservation={openDetail} />
            <button onClick={() => setTab("settings")} data-testid="settings-btn" className="rounded-full border border-gold/30 p-2 text-ivory/80 hover:bg-gold/10" title="Notification settings"><SettingsIcon className="h-4 w-4" /></button>
            <span className="hidden text-xs text-ivory/60 sm:block" data-testid="portal-user-email">{user.email}</span>
            <button data-testid="portal-logout" onClick={() => { logout(); nav("/login"); }} className="inline-flex items-center gap-1.5 rounded-full border border-gold/30 px-4 py-2 text-xs text-gold hover:bg-gold/10">
              <LogOut className="h-3.5 w-3.5" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-6 py-8">
        {user.account_status === "suspended" && (
          <div data-testid="account-suspended-banner" className="mb-6 rounded-2xl border border-orange-500/30 bg-orange-500/5 px-5 py-4">
            <p className="font-mono text-[11px] uppercase tracking-widest text-orange-400">Account suspended</p>
            <p className="mt-1.5 text-sm text-ivory/75">
              Your account access has been paused. Please contact Nanak Chauffeurs if you believe this is a mistake.
            </p>
          </div>
        )}
        {/* scope switcher */}
        <div className="flex flex-wrap items-center gap-2" data-testid="scope-switcher">
          <button onClick={() => { setScope("all"); setTab("reservations"); }} className={`rounded-full px-4 py-2 text-xs font-semibold ${scope === "all" ? "bg-gold text-obsidian-900" : "border border-gold/25 text-ivory/70"}`} data-testid="scope-all">All</button>
          <button onClick={() => { setScope("individual"); setTab("reservations"); }} className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-semibold ${scope === "individual" ? "bg-gold text-obsidian-900" : "border border-gold/25 text-ivory/70"}`} data-testid="scope-individual"><UserIcon className="h-3.5 w-3.5" /> My Trips</button>
          {user.is_driver && (
            <button onClick={() => { setScope("driver"); setTab("reservations"); }} className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-semibold ${scope === "driver" ? "bg-gold text-obsidian-900" : "border border-gold/25 text-ivory/70"}`} data-testid="scope-driver"><CarIcon className="h-3.5 w-3.5" /> My Assignments</button>
          )}
          {memberships.map((m) => (
            <button key={m.company_id} onClick={() => { setScope(m.company_id); setTab(["corporate_admin", "corporate_dispatcher"].includes(m.role) ? "operations" : "reservations"); }} className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-semibold ${scope === m.company_id ? "bg-gold text-obsidian-900" : "border border-gold/25 text-ivory/70"}`} data-testid={`scope-company-${m.company_id}`}>
              <Building2 className="h-3.5 w-3.5" /> {m.company_name}
            </button>
          ))}
        </div>

        <div className="mt-6 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl font-bold text-ivory sm:text-4xl" data-testid="dashboard-title">
              {isManager ? `${activeCompany.company_name} Live Operations` : activeCompany ? `${activeCompany.company_name} Reservations` : scope === "individual" ? "My Reservations" : "All Reservations"}
            </h1>
            {isManager && <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.24em] text-gold/80">Transportation Command Center · Read-only</p>}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} className="inline-flex items-center gap-1.5 rounded-full border border-gold/25 px-4 py-2 text-xs text-ivory/80 hover:bg-gold/10" data-testid="refresh-btn"><RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh</button>
            <button onClick={exportCsv} className="inline-flex items-center gap-1.5 rounded-full border border-gold/25 px-4 py-2 text-xs text-ivory/80 hover:bg-gold/10" data-testid="export-btn"><Download className="h-3.5 w-3.5" /> CSV</button>
          </div>
        </div>

        {/* manager tab bar */}
        {isManager && (
          <div className="mt-4 flex flex-wrap gap-2" data-testid="board-tabs">
            <button onClick={() => setTab("operations")} className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-semibold ${tab === "operations" ? "bg-gold text-obsidian-900" : "border border-gold/25 text-ivory/70"}`} data-testid="tab-operations"><Activity className="h-3.5 w-3.5" /> Operations Board</button>
            <button onClick={() => setTab("reservations")} className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-semibold ${tab === "reservations" ? "bg-gold text-obsidian-900" : "border border-gold/25 text-ivory/70"}`} data-testid="tab-history"><LayoutGrid className="h-3.5 w-3.5" /> All Reservations</button>
            {isCorpAdmin && <button onClick={() => setTab("team")} className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-semibold ${tab === "team" ? "bg-gold text-obsidian-900" : "border border-gold/25 text-ivory/70"}`} data-testid="team-toggle"><Users className="h-3.5 w-3.5" /> Team</button>}
          </div>
        )}

        <div className="mt-4"><SyncHealthBanner health={health} /></div>

        {/* read-only notice */}
        <p className="mt-3 rounded-lg border border-gold/15 bg-obsidian-800 px-4 py-2 text-xs text-ivory/55" data-testid="readonly-notice">
          Viewing only. Reservations are managed in Limo Anywhere — to change a booking, contact dispatch.
        </p>

        {tab === "settings" ? (
          <NotificationSettings />
        ) : tab === "team" && isCorpAdmin ? (
          <TeamPanel companyId={scope} />
        ) : (
          <>
            {/* stats */}
            <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-5">
              <StatCard testid="stat-upcoming" label="Upcoming" value={s.upcoming} accent="#D4AF37" />
              <StatCard testid="stat-active" label="Active" value={s.active} accent="#34D399" />
              <StatCard testid="stat-completed" label="Completed" value={s.completed} accent="#10B981" />
              <StatCard testid="stat-canceled" label="Canceled" value={s.canceled} accent="#EF4444" />
              <StatCard testid="stat-total" label="Total" value={s.total} />
            </div>

            {tab === "operations" && isManager && (
              <ActiveTripsBoard items={(data?.items || []).filter((r) => ["driver_en_route", "driver_arrived", "passenger_on_board", "in_progress"].includes(r.status))} onOpen={openDetail} />
            )}

            {/* filters */}
            <div className="mt-6 flex flex-wrap items-center gap-2">
              <input data-testid="filter-search" placeholder="Search…" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} className={`${sel} w-44`} />
              <select data-testid="filter-status" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} className={sel}>
                <option value="">All statuses</option>
                {Object.entries(STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
              <select data-testid="filter-chauffeur" value={filters.chauffeur} onChange={(e) => setFilters({ ...filters, chauffeur: e.target.value })} className={sel}>
                <option value="">All chauffeurs</option>
                {(data?.filters?.chauffeurs || []).map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <select data-testid="filter-vehicle" value={filters.vehicle} onChange={(e) => setFilters({ ...filters, vehicle: e.target.value })} className={sel}>
                <option value="">All vehicles</option>
                {(data?.filters?.vehicles || []).map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <DateField testid="filter-date-from" value={filters.date_from} onChange={(v) => setFilters({ ...filters, date_from: v })} placeholder="From date" />
              <DateField testid="filter-date-to" value={filters.date_to} onChange={(v) => setFilters({ ...filters, date_to: v })} placeholder="To date" />
            </div>

            {/* reservations list (premium cards) */}
            <div className="mt-5 space-y-3.5" data-testid="reservations-table">
              {(data?.items || []).length === 0 && <p className="rounded-2xl border border-gold/12 bg-obsidian-800 px-5 py-12 text-center text-sm text-ivory/50" data-testid="no-reservations">No reservations to display.</p>}
              {(data?.items || []).map((r) => (
                <ReservationListCard key={r.reservation_id} r={r} onOpen={openDetail} />
              ))}
            </div>
            {scope === "individual" && memberships.length === 0 && (
              <div className="mt-6">
                {showCorpReq ? <CorpRequestForm onSubmit={requestCorp} onCancel={() => setShowCorpReq(false)} /> :
                  <button onClick={() => setShowCorpReq(true)} data-testid="request-corporate-btn" className="rounded-full border border-gold/30 px-5 py-2.5 text-xs text-gold hover:bg-gold/10">Request a corporate account</button>}
              </div>
            )}
          </>
        )}
      </div>

      {detail && <ReservationDetailDrawer r={detail} onClose={() => setDetail(null)} />}
    </main>
  );
}

function CorpRequestForm({ onSubmit, onCancel }) {
  const [name, setName] = useState("");
  return (
    <div className="rounded-2xl border border-gold/15 bg-obsidian-800 p-5">
      <p className="text-sm text-ivory/80">Request a corporate account (reviewed & approved by Nanak Chauffeurs).</p>
      <div className="mt-3 flex gap-2">
        <input data-testid="corp-name-input" placeholder="Company name" value={name} onChange={(e) => setName(e.target.value)} className={`${sel} flex-1`} />
        <button data-testid="corp-request-submit" onClick={() => name && onSubmit(name)} className="rounded-full bg-gold px-5 py-2 text-xs font-semibold text-obsidian-900">Submit</button>
        <button onClick={onCancel} className="rounded-full border border-gold/25 px-4 py-2 text-xs text-ivory/70">Cancel</button>
      </div>
    </div>
  );
}

function ActiveTripsBoard({ items, onOpen }) {
  return (
    <section className="mt-6" data-testid="active-trips-board">
      <h2 className="flex items-center gap-2 font-display text-xl font-semibold text-ivory">
        <Activity className="h-4 w-4 text-gold-light" /> Active Now
        <span className="rounded-full bg-gold/15 px-2 py-0.5 text-xs text-gold-light">{items.length}</span>
      </h2>
      {items.length === 0 ? (
        <p className="mt-3 rounded-2xl border border-gold/10 bg-obsidian-800 px-5 py-8 text-center text-sm text-ivory/50" data-testid="no-active-trips">No active trips right now. Upcoming reservations appear below.</p>
      ) : (
        <div className="mt-3 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((r) => (
            <button key={r.reservation_id} onClick={() => onOpen(r.reservation_id)} data-testid={`active-card-${r.confirmation_number}`} className="rounded-2xl border border-gold/20 bg-obsidian-800 p-5 text-left transition hover:border-gold/50">
              <div className="flex items-center justify-between"><span className="font-mono text-xs text-gold">{r.confirmation_number}</span><StatusBadge status={r.status} /></div>
              <p className="mt-3 font-display text-lg text-ivory">{r.passenger_name}</p>
              <p className="mt-1 text-xs text-ivory/60">{r.pickup_address} → {r.dropoff_address}</p>
              <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-ivory/55">
                <span>Pickup: {fmtDateTime(r.pickup_datetime)}</span>
                <span className="text-right">{r.vehicle}</span>
                <span>{r.chauffeur_name}</span>
                <span className="text-right text-ivory/40">Updated {fmtDateTime(r.source_updated_at)}</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </section>
  );
}
